import { Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { DataService } from '../services/data.service';
import { ConfigurationsService } from '../services/configurations.service';
import { StateInitializerService } from '../services/state-initializer.service';
import { CURRENT_USER_ENDPOINT } from '../config/config.constants';
import { CurrentUserInfo } from '../models/currentUserInfo';
import * as PostActions from '../store/actions';
import { environment } from 'src/environments/environment';

/**
 * Inizializzatore dell'applicazione che carica i dati utente e le configurazioni
 * quando l'utente ha già un token valido (reload pagina o accesso diretto).
 *
 * Questo resolve il problema che al login vengono caricati:
 * - checkDashboardGrant (dashboard custom)
 * - getSignalsCatalogAction (catalogo segnali)
 *
 * Ma se l'utente ricarica la pagina con token valido, questi dati non vengono caricati.
 */
export function initializeApp(
  dataService: DataService,
  apiService: ApiService,
  configurationsService: ConfigurationsService,
  stateInitializer: StateInitializerService
): () => Observable<any> {
  return () => {
    const token = localStorage.getItem('token');

    // Nessun token presente → l'utente andrà alla pagina di login
    if (!token) {
      console.log('🔒 [AppInitializer] No token found, skipping initialization');
      return of(null);
    }

    console.log('🚀 [AppInitializer] Token found, initializing app data...');

    // Token presente → carica tutti i dati come farebbe il login
    return apiService.get(CURRENT_USER_ENDPOINT).pipe(
      tap((userData: any) => {
        const userInfo = userData as CurrentUserInfo;
        console.log('👤 [AppInitializer] User data loaded:', userInfo.user?.login);

        // Salva le informazioni utente
        dataService.setCurrentUserInfo(userInfo);

        // Login-as e fine impersonificazione fanno localStorage.clear() + reload: la chiave
        // 'language' viene persa e getLanguage() cade sul fallback 'en' (rimanendoci anche dopo
        // essere usciti). Se manca, la ripristiniamo dalle preferenze utente così la lingua segue
        // l'identità corrente. La ripristiniamo SOLO se assente: su reload normale rispettiamo il
        // valore già in localStorage (il language-selector persiste la scelta manuale solo lì).
        if (!localStorage.getItem('language')) {
          const lang = userInfo.user?.preferences?.language?.label;
          if (lang) {
            dataService.setLanguage(lang);
          }
        }

        // Inizializza lo store usando il servizio centralizzato
        stateInitializer.initializeStoreState(userInfo);
      }),
      switchMap((userData: any) => {
        const userInfo = userData as CurrentUserInfo;
        console.log('📊 [AppInitializer] Loading dashboard grants...');

        // Carica i permessi dashboard e le dashboard custom
        return configurationsService.checkDashboardGrant() ?? of({ user: userInfo });
      }),
      switchMap((res: any) => {
        const user: CurrentUserInfo = res.user;
        const userId = user.user?.uid;
        const customerId = user.customer?.uid;

        if (userId && customerId) {
          console.log('🎨 [AppInitializer] Loading graphics configuration...');
          // Carica e applica la configurazione grafica PRIMA che l'app diventi visibile
          return stateInitializer.loadAndApplyGraphicsConfiguration(userId, customerId).pipe(
            tap(() => {
              // Carica il catalogo segnali (solo per ambienti non dev/test)
              if (environment.environmentName !== 'dev') {
                stateInitializer.loadSignalsCatalog();
              }
            }),
            switchMap(() => of(res)) // Passa il risultato originale
          );
        } else {
          console.warn('⚠️ [AppInitializer] Missing user or customer ID, skipping graphics configuration');
          // Carica comunque il catalogo segnali se possibile
          if (environment.environmentName !== 'dev' && userId) {
            stateInitializer.loadSignalsCatalog();
          }
          return of(res);
        }
      }),
      tap(() => {
        console.log('✅ [AppInitializer] App initialization completed successfully');
      }),
      catchError((error) => {
        console.error('❌ [AppInitializer] Error during initialization:', error);

        // In caso di errore (es. token scaduto), pulisci localStorage
        // e lascia che l'AuthGuard reindirizzi al login
        localStorage.clear();

        return of(null);
      })
    );
  };
}
